import { withNamespaces } from "react-i18next";
import { Table, UncontrolledTooltip } from "reactstrap";
import { KeysTitles } from "../../../../constants";
import { RiInformationLine } from "react-icons/ri";

// Define sets for percentages and money
const percentageKeys = new Set([
    "equity_in_percentage",
    "pre_tax_profit_margin",
    "return_on_capital_employed",
    "liquidity_ratio_or_acid_test",
    "sales_or_net_working_capital",
    "return_on_net_assets_employed",
    "gearing",
    "current_ratio",
    "total_debt_ratio",
    "current_debt_ratio",
    "stock_turnover_ratio"
]);

const moneyKeys = new Set([
    "cash",
    "investments",
    "total_assets",
    "share_premium",
    "finished_goods",
    "other_reserves",
    "trade_payables",
    "revenue_reserves",
    "total_inventories",
    "total_liabilities",
    "total_receivables",
    "trade_receivables",
    "total_fixed_assets",
    "plant_and_machinery",
    "other_current_assets",
    "total_current_assets",
    "total_tangible_assets",
    "other_loans_or_finance",
    "called_up_share_capital",
    "total_intangible_assets",
    "total_other_fixed_assets",
    "miscellaneous_liabilities",
    "total_current_liabilities",
    "total_shareholders_equity",
    "total_long_term_liabilities",
    "revenue",
    "operating_costs",
    "retained_profit",
    "financial_income",
    "operating_profit",
    "profit_after_tax",
    "profit_before_tax",
    "wages_and_salaries",
    "net_worth",
    "working_capital",
    "raw_materials",
    "other_inventories",
    "land_and_buildings",
    "other_tangible_assets",
    "miscellaneous_receivables",
    "miscellaneous_fixed_assets",
    "work_in_progress",
    "other_intangible_assets",
    "tax",
    "depreciation",
    "pension_costs",
    "financial_expenses"
]);

const isComparableValue = (keyName) => {
    return percentageKeys.has(keyName) || moneyKeys.has(keyName);
}

// Format numbers with currency
const formatFinancialValue = (key, value, currency = null) => {
    if (value === null || value === undefined || value === "") return "-";

    const num = Number(value);
    if (isNaN(num)) return value;

    if (percentageKeys.has(key)) {
        return `${num.toFixed(2)}%`;
    } else if (moneyKeys.has(key) && currency) {
        return new Intl.NumberFormat("en-US", {
            style: "currency",
            currency,
            maximumFractionDigits: 2
        }).format(num);
    } else {
        return new Intl.NumberFormat("en-US", { maximumFractionDigits: 2 }).format(num);
    }
};

// Utility to get all row keys for a section across years
const getRowKeys = (data, section) => {
    const keys = new Set();
    data.forEach(item => {
        if (item[section]) {
            Object.keys(item[section]).forEach(k => keys.add(k));
        }
    });
    return Array.from(keys);
};

const FinancialTable = ({ 
    t, 
    data 
}) => {
    const sections = ["balance_sheet", "profit_and_loss", "ratios", "other_financials"];

    return (
        sections.map(section => {
            const hasSection = data.some(d => d[section] && Object.keys(d[section]).length > 0);
            if (!hasSection) return null;

            const rowKeys = getRowKeys(data, section);

            return (
                <div key={section} className="mb-4">
                    <div className="mb-2 text-center" style={{ fontWeight: "bold" }}>
                        {section.replace("_", " ").replace(/\b\w/g, l => l.toUpperCase())}
                    </div>

                    <Table striped bordered hover size="sm">
                        <thead>
                            <tr>
                                <th style={{
                                    width: '20%'
                                }}>{t('Metric / Description')}</th>
                                {data.map((yearItem, idx) => (
                                    <th
                                        key={idx}
                                    >
                                        {new Date(yearItem.year_end_date).getFullYear()}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <th>
                                    {t('Currency')}{' '}
                                    <span
                                        id={`tooltip-financial-currency-${section}`}
                                        style={{ cursor: "pointer" }}>
                                        <RiInformationLine />
                                    </span>
                                    <UncontrolledTooltip
                                        target={`tooltip-financial-currency-${section}`}
                                    >
                                        {t('Currency the figures are displayed in.')}
                                    </UncontrolledTooltip>
                                </th>
                                {data.map((yearItem, idx) => (
                                    <td
                                        key={idx}
                                    >
                                        {yearItem.currency}
                                    </td>
                                ))}
                            </tr>

                            <tr>
                                <th>{t('Number of Weeks')}</th>
                                {data.map((yearItem, idx) => (
                                    <td
                                        key={idx}
                                        className="text-end"
                                    >
                                        {yearItem.number_of_weeks}
                                    </td>
                                ))}
                            </tr>

                            {rowKeys.map(rowKey => (
                                <tr key={rowKey}>
                                    <th>
                                        {t(KeysTitles[rowKey]?.title || rowKey)}
                                        {KeysTitles[rowKey]?.description ? (
                                            <>
                                                {' '}
                                                <span
                                                    id={`tooltip-financials-${section}-${rowKey}`}
                                                    style={{ cursor: "pointer" }}>
                                                    <RiInformationLine />
                                                </span>
                                                <UncontrolledTooltip
                                                    target={`tooltip-financials-${section}-${rowKey}`}
                                                >
                                                    {t(KeysTitles[rowKey].description)}
                                                </UncontrolledTooltip>
                                            </>
                                        ) : null}
                                    </th>

                                    {data.map((yearItem, idx) => {
                                        const value = yearItem[section]?.[rowKey];
                                        return (
                                            <td
                                                key={idx}
                                                className="text-end"
                                            >
                                                {formatFinancialValue(rowKey, value, yearItem.currency)}
                                            </td>
                                        );
                                    })}
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                </div>
            );
        })
    )
}

export default withNamespaces()
    (
        FinancialTable
    );